/**
 * Weather overlay — standard meteorological wind barbs over the chart for the
 * selected weather source. One source at a time, the same way the tidal-stream
 * overlay works, so the two swap cleanly while staying on the chart.
 *
 * Barb convention: the staff points in the direction the wind comes FROM; speed
 * is read off the feathers near the outer end (half barb = 5 kn, full = 10 kn,
 * pennant = 50 kn), rounded to the nearest 5. Calm (under CALM_KN) shows an open
 * ring, no staff. Drawn in a marine blue so it never reads as the magenta tide
 * arrows. Synthetic training data here; not a real forecast.
 *
 * The barb geometry itself (staff + feathers + pennants) lives in windBarb.ts so
 * the own-ship wind vector draws the same barb; this module adds the station dot
 * and the map layers, and rebuilds as the map zooms (barbScaleForZoom) so the barb
 * holds a roughly constant on-screen size instead of ballooning as we zoom in.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import type { WindSample } from '@/weather';
import { windBarbFeatures, barbScaleForZoom, barbScaleForGrid, CALM_KN } from './windBarb';
import { windArrowFeatures, windColourExpression } from './windArrow';

/** How the field is drawn. Arrows by default; barbs for anyone who prefers them. */
export type WindStyle = 'arrow' | 'barb';
import { rhumbDistanceNm } from '@/nav';

export { barbScaleForZoom } from './windBarb';

const SOURCE_ID = 'weather-wind';
const WX_COLOR = '#0b6cb5';

function build(
  samples: WindSample[],
  on: boolean,
  scale = 1,
  style: WindStyle = 'arrow',
): FeatureCollection {
  if (!on) return { type: 'FeatureCollection', features: [] };
  const features: Feature[] = [];
  if (style === 'arrow') {
    for (const s of samples) {
      for (const f of windArrowFeatures(s.position, s.dirFromDeg, s.speedKn, scale)) {
        features.push(f);
      }
    }
    return { type: 'FeatureCollection', features };
  }
  for (const s of samples) {
    // The station dot: an open ring when calm (no staff to hang feathers on), a
    // small filled dot otherwise so the reading has a clear anchor point.
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: s.position },
      properties: { kind: 'dot', calm: s.speedKn < CALM_KN, speedKn: Math.round(s.speedKn) },
    });
    for (const f of windBarbFeatures(s.position, s.dirFromDeg, s.speedKn, scale, { line: 'barb', pennant: 'pennant' })) {
      features.push(f);
    }
  }
  return { type: 'FeatureCollection', features };
}

/**
 * The spacing between neighbouring grid points, in NM.
 *
 * The smallest gap from the first point to any other, which on a regular grid is
 * one cell. Used to size the barbs so they fit their own cell rather than being
 * sized by zoom, which held them at a constant fraction of the screen and left
 * them overlapping at every zoom level.
 */
function gridSpacingNm(samples: WindSample[]): number {
  if (samples.length < 2) return 0;
  const a = samples[0].position;
  let best = Infinity;
  for (let i = 1; i < samples.length; i++) {
    const d = rhumbDistanceNm(a, samples[i].position);
    if (d > 0 && d < best) best = d;
  }
  return Number.isFinite(best) ? best : 0;
}

/** Barb scale for the FIELD: sized to the grid, not to the zoom. */
function fieldScale(samples: WindSample[], map: MaplibreMap): number {
  const spacing = gridSpacingNm(samples);
  // One lone sample has no grid to fit, so fall back to the zoom rule.
  return spacing > 0 ? barbScaleForGrid(spacing) : barbScaleForZoom(map.getZoom());
}

export function ensureWeatherLayer(
  map: MaplibreMap,
  on: boolean,
  samples: WindSample[],
  style: WindStyle = 'arrow',
): void {
  const scale = fieldScale(samples, map);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(samples, on, scale, style) });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(build(samples, on, scale, style));
  }
  // --- Arrow style: a coloured arrow along the flow, with the speed at its base.
  if (!map.getLayer('weather-arrow')) {
    map.addLayer({
      id: 'weather-arrow',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'arrow'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // Bolder than the barbs were, which is the other half of what was asked
        // for: at arm's length in daylight a 1.6 px line is not there.
        'line-width': 2.8,
        'line-color': windColourExpression() as unknown as string,
      },
    });
  }
  if (!map.getLayer('weather-arrow-base')) {
    map.addLayer({
      id: 'weather-arrow-base',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'arrow-base'],
      paint: {
        'circle-radius': 9,
        'circle-color': windColourExpression() as unknown as string,
        'circle-stroke-color': 'rgba(255,255,255,0.95)',
        'circle-stroke-width': 1.4,
      },
    });
  }
  if (!map.getLayer('weather-arrow-speed')) {
    map.addLayer({
      id: 'weather-arrow-speed',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'arrow-base'],
      layout: {
        'text-field': ['to-string', ['get', 'speedKn']],
        'text-font': ['Open Sans Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        // Dark on the pale end of the ramp, white on the hot end, so the number
        // stays legible against its own circle rather than needing a halo that
        // would fatten every glyph.
        'text-color': ['step', ['get', 'speedKn'], '#0b1f3d', 17, '#ffffff'],
      },
    });
  }
  if (!map.getLayer('weather-pennant')) {
    map.addLayer({
      id: 'weather-pennant',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'pennant'],
      paint: { 'fill-color': WX_COLOR, 'fill-opacity': 0.9 },
    });
  }
  if (!map.getLayer('weather-barb')) {
    map.addLayer({
      id: 'weather-barb',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'barb'],
      paint: { 'line-color': WX_COLOR, 'line-width': 1.6 },
    });
  }
  if (!map.getLayer('weather-dot')) {
    map.addLayer({
      id: 'weather-dot',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'dot'],
      paint: {
        'circle-radius': ['case', ['get', 'calm'], 4, 2],
        'circle-color': ['case', ['get', 'calm'], 'rgba(11,108,181,0)', WX_COLOR],
        'circle-stroke-color': WX_COLOR,
        'circle-stroke-width': ['case', ['get', 'calm'], 1.4, 0],
      },
    });
  }
}

export function updateWeatherLayer(
  map: MaplibreMap,
  on: boolean,
  samples: WindSample[],
  style: WindStyle = 'arrow',
): void {
  const src = map.getSource(SOURCE_ID);
  if (src) {
    (src as maplibregl.GeoJSONSource).setData(build(samples, on, fieldScale(samples, map), style));
  }
}

// --- Pinned wind barb --------------------------------------------------------
// A single wind barb the skipper pinned to a tapped spot (the "Wind here" ring
// action). It lives on its own source so it shows whether or not the full
// weather overlay is on, and clears when they tap elsewhere. Drawn a touch
// bolder than the overlay barbs so the one they asked for stands out.
const PIN_SOURCE_ID = 'pinned-wind';
const PIN_COLOR = '#0a4d84';

function pinData(sample: WindSample | null, scale = 1): FeatureCollection {
  return sample ? build([sample], true, scale) : { type: 'FeatureCollection', features: [] };
}

export function ensurePinnedWindLayer(map: MaplibreMap, sample: WindSample | null): void {
  const scale = barbScaleForZoom(map.getZoom());
  if (!map.getSource(PIN_SOURCE_ID)) {
    map.addSource(PIN_SOURCE_ID, { type: 'geojson', data: pinData(sample, scale) });
  } else {
    (map.getSource(PIN_SOURCE_ID) as maplibregl.GeoJSONSource).setData(pinData(sample, scale));
  }
  if (!map.getLayer('pinned-wind-pennant')) {
    map.addLayer({
      id: 'pinned-wind-pennant',
      type: 'fill',
      source: PIN_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'pennant'],
      paint: { 'fill-color': PIN_COLOR, 'fill-opacity': 0.95 },
    });
  }
  if (!map.getLayer('pinned-wind-barb')) {
    map.addLayer({
      id: 'pinned-wind-barb',
      type: 'line',
      source: PIN_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'barb'],
      paint: { 'line-color': PIN_COLOR, 'line-width': 2.2 },
    });
  }
  if (!map.getLayer('pinned-wind-dot')) {
    map.addLayer({
      id: 'pinned-wind-dot',
      type: 'circle',
      source: PIN_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'dot'],
      paint: {
        'circle-radius': ['case', ['get', 'calm'], 5, 3],
        'circle-color': ['case', ['get', 'calm'], 'rgba(10,77,132,0)', PIN_COLOR],
        'circle-stroke-color': PIN_COLOR,
        'circle-stroke-width': ['case', ['get', 'calm'], 1.6, 0],
      },
    });
  }
}

export function updatePinnedWindLayer(map: MaplibreMap, sample: WindSample | null): void {
  const src = map.getSource(PIN_SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(pinData(sample, barbScaleForZoom(map.getZoom())));
}

/**
 * Re-apply the wind colour ladder to the arrow layers after the thresholds
 * change (fb-msmaz0fj-3). The expressions are baked into paint at creation, so
 * a settings change has to be pushed; a colour change should cost a colour
 * change, not a layer rebuild.
 */
export function applyWindColourThresholds(map: MaplibreMap): void {
  try {
    if (map.getLayer('weather-arrow'))
      map.setPaintProperty('weather-arrow', 'line-color', windColourExpression() as unknown as string);
    if (map.getLayer('weather-arrow-base'))
      map.setPaintProperty('weather-arrow-base', 'circle-color', windColourExpression() as unknown as string);
  } catch {
    // Mid-styleswap race: the ensure path re-applies on the next pass.
  }
}
